import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
// External components
import {
  Box,
  Button,
  CardContent,
  Card,
  Typography,
  Divider,
  Grid,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// GraphQL
import { useApolloClient } from "@apollo/client";
import { client_EB_api } from "../../graphql/client";
import {
  GET_EB_BY_ID,
  RESEND_EMAIL,
  GET_CREDIT_NOTES,
  GET_DEBIT_NOTES,
} from "../../graphql";
// Components
import { Table } from "../../components/DataTable/components";
import { CustomLoading, PersonSection, SendBillScreen } from "../../components";
// Hooks
import useElectronicBill from "../../hooks/useElectronicBill";
// Context
import { useElectronic } from "../../context/ElectronicContext";
import { useModal } from "../../context";
// Enums
import DEFAULT_COLUMNS from "./Enums/Columns";
import {
  BILL_TYPES_URL_PARAMS,
  ELECTRONIC_BILLS_DOCUMENT_TYPES,
} from "../../Enums/ElectronicBill";
// SCSS
import "./EBViewPage.scss";
import { normalizeEBillDetails } from "./EBviewHelper";
import { customToast } from "../../utils";
import DownloadCard from "../IncomePage/components/DownloadCard";

function EBViewPage() {
  const { setModalOpen: setDownloadModalOpen } = useModal();
  const [electronicBillForReview, setElectronicBillForReview] = useState();
  const { id, viewType, billType } = useParams();
  const {
    useCoinDetail,
    LineDetailPlace,
    resetElectronicBill,
    resetExportationBasicInfo,
  } = useElectronic();
  const { getElectronicBillForReview, formatBillDetailToTable } =
    useElectronicBill();
  const navigate = useNavigate();
  const { toExchangeRate, currentSymbolCoin, resetDefaultCoin } = useCoinDetail;
  const { setModalOpen } = useModal();

  const checkBillType = useCallback(
    billTypeEnumType => billType === billTypeEnumType,
    [billType],
  );
  const isDebitNote = () => checkBillType(BILL_TYPES_URL_PARAMS.DEBIT_NOTE);
  const isCreditNote = () => checkBillType(BILL_TYPES_URL_PARAMS.CREDIT_NOTE);
  const isBuyElectronicBill = () =>
    checkBillType(BILL_TYPES_URL_PARAMS.BUY_BILL);
  const isExportationElectronicBill = () =>
    checkBillType(BILL_TYPES_URL_PARAMS.EXPORTATION_BILL);
  const getCreditNote = async bill_id => {
    const resp = await client_EB_api.query({
      query: GET_CREDIT_NOTES,
      variables: {
        whereParams: {
          comparisons: [["id", "=", bill_id]],
        },
      },
    });

    if (!resp?.data?.getCreditNotesNew) {
      return null;
    }
    const temp = {
      ...resp.data.getCreditNotesNew[0],
      details: resp.data.getCreditNotesNew[0].CreditNoteDetails,
    };
    //    delete temp.CreditNoteDetails;
    return temp;
  };

  const getDebitNote = async bill_id => {
    const resp = await client_EB_api.query({
      query: GET_DEBIT_NOTES,
      variables: {
        whereParams: {
          comparisons: [["id", "=", bill_id]],
        },
      },
    });

    if (!resp?.data?.getDebitNotes) {
      return null;
    }
    const temp = {
      ...resp.data.getDebitNotes[0],
      details: resp.data.getDebitNotes[0].DebitNoteDetails,
    };
    // delete temp.DebitNoteDetails;
    return temp;
  };

  const selectGetBillFunction = useCallback(() => {
    if (checkBillType(BILL_TYPES_URL_PARAMS.DEBIT_NOTE)) {
      return async e => getDebitNote(e);
    }
    if (checkBillType(BILL_TYPES_URL_PARAMS.CREDIT_NOTE)) {
      return async e => getCreditNote(e);
    }
    if (checkBillType(BILL_TYPES_URL_PARAMS.BUY_BILL)) {
      return async e =>
        normalizeEBillDetails(await getElectronicBillForReview(e));
    }
    return async e =>
      normalizeEBillDetails(await getElectronicBillForReview(e));
  }, [checkBillType, getElectronicBillForReview]);

  const fetchElectronicBillForReview = useCallback(async () => {
    if (!id) return;
    const getEB = selectGetBillFunction();
    const resp = await getEB(id);
    if (!resp) {
      customToast.error("No se encontró la factura electrónica");
      return;
    }
    setElectronicBillForReview(resp);
  }, [id, selectGetBillFunction]);

  useEffect(() => {
    if (electronicBillForReview) return;
    fetchElectronicBillForReview();
  }, [electronicBillForReview, fetchElectronicBillForReview]);
  const client = useApolloClient();

  const getElectronicBillById = async idEB => {
    const electronicBill = await client.query({
      query: GET_EB_BY_ID,
      variables: {
        id: idEB,
      },
      fetchPolicy: "no-cache",
    });
    return electronicBill;
  };

  const handleSendEmails = async receivers => {
    let idElectronicBill = 0;
    const features = [];

    const electronicBill = await getElectronicBillById(id);

    let creditNote = null;
    idElectronicBill = electronicBill?.data?.EBill?.Bill?.id;

    if (
      electronicBill?.data?.EBill?.ElectronicBillDocumentType?.id ===
      `${ELECTRONIC_BILLS_DOCUMENT_TYPES.CREDIT_NOTE}`
    ) {
      creditNote = await getElectronicBillById(
        electronicBill?.data?.EBill?.ReferencedDocument?.id,
      );
      idElectronicBill = creditNote?.data?.EBill?.Bill?.id;
      electronicBill?.data?.EBill?.ElectronicBillDetail.map(detail =>
        creditNote?.data?.EBill?.ElectronicBillDetail.map(
          creditDetail =>
            detail.ProductFeature.id === creditDetail.ProductFeature.id &&
            features.push(detail.ProductFeature.id),
        ),
      );
    }

    await client_EB_api.mutate({
      mutation: RESEND_EMAIL,
      variables: {
        id: idElectronicBill,
        idElectronicBillFeatures: features,
        to: receivers,
      },
    });
    customToast.success("Email enviado con éxito!");
  };

  const openSendEmailModal = () => {
    setModalOpen(
      true,
      <SendBillScreen
        receptorRequired
        billingEmail={receptor?.Email}
        handleCloseModal={() => setModalOpen(false)}
        handleSend={handleSendEmails}
        place={LineDetailPlace}
      />,
    );
  };

  const receptor =
    electronicBillForReview?.Bill?.User?.UserBillingProfile ||
    electronicBillForReview?.ReferencedDocument?.Bill?.User?.UserBillingProfile;

  const handleDownload = async () => {
    setDownloadModalOpen(
      true,
      <DownloadCard
        incomeID={electronicBillForReview?.Key}
        setModalOpen={setDownloadModalOpen}
      />,
    );
  };

  const handleNewElectronicBill = () => {
    resetElectronicBill();
    resetDefaultCoin();
    navigate("/electronic-bills");
  };
  const handleNewBuyElectronicBill = () => {
    resetElectronicBill();
    resetDefaultCoin();
    navigate("/buy-electronic-bills");
  };

  const handleNewExportationElectronicBill = () => {
    resetElectronicBill();
    resetDefaultCoin();
    resetExportationBasicInfo();
    navigate("/exportation-bill");
  };
  const productsFormattedToTable = electronicBillForReview?.details?.map(
    (productFormatted, index) =>
      formatBillDetailToTable(
        {
          ...productFormatted,
          index: index + 1,
        },
        currentSymbolCoin,
      ),
  );

  const totalAmounts = useMemo(
    () => [
      {
        id: "TotalServicesTaxed",
        title: "Servicios gravados",
        value: electronicBillForReview?.TotalServicesTaxed,
      },
      {
        id: "TotalServicesExempt",
        title: "Servicios exentos",
        value: electronicBillForReview?.TotalServicesExempt,
      },
      {
        id: "TotalGoodsTaxed",
        title: "Bienes gravados",
        value: electronicBillForReview?.TotalGoodsTaxed,
      },
      {
        id: "TotalGoodsExempt",
        title: "Bienes exentos",
        value: electronicBillForReview?.TotalGoodsExempt,
      },
      {
        id: "Discount",
        title: "Total de descuentos",
        value: electronicBillForReview?.TotalDiscount,
      },
      {
        id: "TotalNetSell",
        title: "Precio Neto (Subtotal)",
        value: electronicBillForReview?.TotalNetSell || 0,
      },
      {
        id: "total-7",
        title: "Más IVA",
        value: electronicBillForReview?.TotalTaxes,
      },
      { id: "total-8", title: "Más IVA Bienes Usados", value: 0 },
      { id: "total-9", title: "Más Otros Impuestos", value: 0 },
      {
        id: "total-10",
        title: "Valor Total de la Factura",
        value: electronicBillForReview?.TotalBill,
      },
      { id: "total-11", title: "Menos IVA Devuelto", value: 0 },
      { id: "total-12", title: "Más Otros Cargos", value: 0 },
      {
        id: "TotalSell",
        title: "Total del Comprobante",
        value: electronicBillForReview?.TotalSell || 0,
      },
    ],
    [electronicBillForReview],
  );

  return electronicBillForReview ? (
    <Box
      sx={{
        display: "flex",
        gap: "1rem",
        flexDirection: "column",
      }}
    >
      <Card
        color="primary"
        sx={{
          paddingY: "1rem",
          paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
        variant="outlined"
      >
        <Typography fontWeight="bold" typography="modeColor" variant="h6">
          Resumen de factura electrónica
        </Typography>
        <CardContent
          sx={{
            padding: 0,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: "1rem",
            justifyContent: "space-between",
          }}
        >
          <CardContent
            sx={{
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <div className="content-flex-row-start">
              <strong>Clave:</strong>
              <p>{`  ${electronicBillForReview.Key}`}</p>
            </div>
            <div className="content-flex-row-start">
              <strong>Consecutivo:</strong>
              <p>{`  ${electronicBillForReview?.InternConsecutive || "--"}`}</p>
            </div>
            <div className="content-flex-row-start">
              <strong>Condición de venta:</strong>
              <p>{`${
                electronicBillForReview?.SellConditionMH?.Name_SellCondition ||
                "--"
              }`}</p>
            </div>
            <div className="content-flex-row-start">
              <strong>Tipo de documento:</strong>
              <p>
                {electronicBillForReview.ElectronicBillDocumentType
                  ?.Description || ""}
              </p>
            </div>
            <div className="content-flex-row-start">
              <p>
                <strong>Moneda:</strong>
                {electronicBillForReview?.Coin?.Name_Coin}
              </p>
            </div>
          </CardContent>
          <CardContent
            sx={{
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <div className="content-flex-row-start">
              <strong>Plazo crédito: --- </strong>
              <p>
                Fecha{!electronicBillForReview.EmitedDay && " (Tentativa)"}:{" "}
                {electronicBillForReview.EmitedDay
                  ? new Date(
                      electronicBillForReview.EmitedDay,
                    ).toLocaleDateString()
                  : new Date().toLocaleDateString().concat(" Verificando...")}
              </p>
            </div>
            <div className="content-flex-row-start">
              <strong>Medio de pago:</strong>
              <p>
                {` ${electronicBillForReview.PaymentMethodMH?.Name_PaymentMethod}`}
              </p>
            </div>
            <div className="content-flex-row-start">
              <strong>Tipo de cambio:</strong>
              <p>
                {electronicBillForReview?.Coin?.Buy_Coin
                  ? (1 / electronicBillForReview.Coin.Buy_Coin).toFixed(2)
                  : "--"}
              </p>
            </div>
          </CardContent>
        </CardContent>
      </Card>
      {(isCreditNote() || isDebitNote()) &&
        electronicBillForReview?.ReferenceReason && (
          <Card
            color="primary"
            sx={{
              paddingY: "1rem",
              paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography fontWeight="bold" typography="modeColor" variant="h6">
              Detalle de nota
            </Typography>

            <div className="content-flex-row-start">
              <strong>Código:</strong>
              <p>
                {
                  electronicBillForReview?.ElectronicBillReferenceType
                    .Document_Type
                }
              </p>
            </div>
            <div className="content-flex-row-start">
              <strong>Razón:</strong>
              <p>{electronicBillForReview?.ReferenceReason}</p>
            </div>
          </Card>
        )}
      <div className="content-grid-column">
        {electronicBillForReview?.BillingProfile && (
          <Card
            color="primary"
            sx={{
              paddingY: "1rem",
              paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <Typography fontWeight="bold" typography="modeColor" variant="h6">
              Datos emisor
            </Typography>
            <PersonSection
              person={{
                ...electronicBillForReview?.BillingProfile,
                activityEmitter:
                  electronicBillForReview.Bill.fk_activity_emitter,
              }}
            />
          </Card>
        )}

        {receptor && (
          <Card
            color="primary"
            sx={{
              paddingY: "1rem",
              paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <Typography fontWeight="bold" typography="modeColor" variant="h6">
              Datos Receptor
            </Typography>
            <PersonSection
              isExportationBill={isExportationElectronicBill()}
              person={{
                ...receptor,
                activityReceiver:
                  electronicBillForReview?.Bill?.fk_activity_receiver,
              }}
            />
          </Card>
        )}
      </div>

      <Card
        color="primary"
        sx={{
          paddingY: "1rem",
          paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography fontWeight="bold" typography="modeColor" variant="h6">
          Detalles
        </Typography>
        <Table columns={DEFAULT_COLUMNS} data={productsFormattedToTable} />
      </Card>

      <Card
        color="primary"
        sx={{
          paddingY: "1rem",
          paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
          display: "flex",
          gap: "28px",
          flexDirection: "column",
        }}
      >
        <Typography fontWeight="bold" typography="modeColor" variant="h6">
          Totales
        </Typography>
        <Grid container spacing="55px">
          <Grid
            item
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            xs={5}
          >
            [Sección para Desglose de impuestos (WIP)]
          </Grid>
          <Grid item xs={2}>
            <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
          </Grid>
          <Grid
            item
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "10px",
            }}
            xs={5}
          >
            {totalAmounts.map(totalAmount => (
              <Box
                key={totalAmount.id}
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                  {totalAmount.title}
                </Typography>
                <Typography sx={{ fontSize: "14px" }}>
                  {currentSymbolCoin}{" "}
                  {toExchangeRate(totalAmount.value)?.toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Grid>
        </Grid>
      </Card>

      <Card
        color="primary"
        sx={{
          paddingY: "1rem",
          paddingX: { xs: "1rem", sm: "1rem", md: "2.5rem" },
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          justifyContent: "end",
        }}
      >
        <strong> Total de la factura: </strong>
        <p>0.00</p>
      </Card>

      <div className="content-flex-end">
        <Button
          color="primary"
          size="large"
          variant="outlined"
          onClick={() => handleDownload(electronicBillForReview)}
        >
          Descargar factura
        </Button>

        {viewType === "created" && (isDebitNote() || isCreditNote()) && (
          <Button
            color="primary"
            size="large"
            variant="contained"
            onClick={async () =>
              navigate(
                `/electronic-bills/${electronicBillForReview.id}/${isCreditNote() ? "CreditNote" : "DebitNote"}`,
              )
            }
          >
            Generar otra nota de {isCreditNote() ? "crédito" : "débito"}
          </Button>
        )}

        {viewType === "created" &&
          isExportationElectronicBill() &&
          !isDebitNote() &&
          !isCreditNote() && (
            <Button
              color="primary"
              size="large"
              variant="contained"
              onClick={handleNewExportationElectronicBill}
            >
              Realizar otra factura
            </Button>
          )}

        {viewType === "created" && isBuyElectronicBill() && (
          <Button
            color="primary"
            size="large"
            variant="contained"
            onClick={() => handleNewBuyElectronicBill()}
          >
            Realizar otra factura
          </Button>
        )}
        {viewType === "created" &&
          !isCreditNote() &&
          !isDebitNote() &&
          !isBuyElectronicBill() &&
          !isExportationElectronicBill() && (
            <Button
              color="primary"
              size="large"
              variant="contained"
              onClick={() => handleNewElectronicBill()}
            >
              Realizar otra factura
            </Button>
          )}
        {id && viewType === "view" && (
          <Button
            color="primary"
            size="large"
            variant="contained"
            onClick={() =>
              openSendEmailModal(
                id,
                setModalOpen,
                receptor?.Email,
                LineDetailPlace,
                getElectronicBillById,
              )
            }
          >
            <FontAwesomeIcon icon="envelope" />
            Enviar correo
          </Button>
        )}
      </div>
    </Box>
  ) : (
    <CustomLoading
      centered
      vCentered
      errorTexts={["Lo sentimos", "Ha ocurrido un error validando los datos"]}
      loadingState={!electronicBillForReview ? "loading" : "warning"}
      loadingTexts={["Recuperando información", "Por favor, espera..."]}
      warningTexts={["Algo inesperado ha ocurrido"]}
    />
  );
}

EBViewPage.propTypes = {};

EBViewPage.defaultProps = {};
export default EBViewPage;
